import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { PdfJsPages } from "@/components/pdf/PdfJsPages";

function asPdfBlob(blob: Blob): Blob {
  return blob.type === "application/pdf"
    ? blob
    : new Blob([blob], { type: "application/pdf" });
}

export function MontagePdfEmbed({
  loadKey,
  loadPdf,
  title = "PDF",
  emptyMessage,
  className,
  paperKey,
}: {
  /** Wechselt bei neuem Dokument, damit neu geladen wird. */
  loadKey: string;
  loadPdf: () => Promise<Blob>;
  title?: string;
  /** Wenn gesetzt und loadPdf fehlschlägt mit „nicht vorhanden“, diese Meldung. */
  emptyMessage?: string;
  className?: string;
  /** Persistiert erkannte/überschriebene Papierformate pro Blatt. */
  paperKey?: string;
}) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const urlRef = useRef<string | null>(null);
  const loadPdfRef = useRef(loadPdf);
  loadPdfRef.current = loadPdf;
  urlRef.current = url;

  useEffect(() => {
    return () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMissing(false);
    setBlob(null);

    void loadPdfRef
      .current()
      .then((raw) => {
        const pdfBlob = asPdfBlob(raw);
        const next = URL.createObjectURL(pdfBlob);
        if (cancelled) {
          URL.revokeObjectURL(next);
          return;
        }
        setBlob(pdfBlob);
        setUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return next;
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : "PDF konnte nicht geladen werden.";
        const lower = message.toLowerCase();
        if (
          lower.includes("keine auftragsbestätigung") ||
          lower.includes("nicht gefunden") ||
          lower.includes("404")
        ) {
          setMissing(true);
          setError(emptyMessage || message);
          setBlob(null);
          setUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
          });
          return;
        }
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loadKey, emptyMessage]);

  const showSpinner = loading && !url && !blob;
  const ready = Boolean(url && blob);

  return (
    <section
      className={clsx(
        "relative flex min-h-[min(50vh,28rem)] flex-col overflow-hidden rounded-xl border border-separator bg-fill-quaternary/15 sm:min-h-[min(72vh,56rem)]",
        className,
      )}
    >
      {showSpinner ? (
        <div className="flex min-h-[min(50vh,28rem)] flex-1 items-center justify-center sm:min-h-[min(72vh,56rem)]">
          <Spinner />
        </div>
      ) : null}
      {(error || missing) && !ready ? (
        <p
          className={clsx(
            "flex min-h-[12rem] flex-1 items-center justify-center px-4 text-center text-sm",
            missing ? "text-label-secondary" : "text-status-red",
          )}
        >
          {error}
        </p>
      ) : null}

      {ready && blob ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-separator bg-card/80 px-3 py-2">
            <span className="truncate text-xs font-medium text-label-secondary">
              {title}
            </span>
            {url ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
              >
                Extern öffnen
              </Button>
            ) : null}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3 timesheet-flow-scroll">
            <PdfJsPages
              blob={blob}
              title={title}
              paperKey={paperKey}
              showPaperSize
            />
          </div>
        </div>
      ) : null}

      {loading && ready ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center pt-3">
          <span className="rounded-full bg-card/90 px-2.5 py-1 text-[11px] text-label-secondary shadow-sm ring-1 ring-separator/40">
            PDF wird aktualisiert …
          </span>
        </div>
      ) : null}
    </section>
  );
}
